import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
// Import the CSS
import './HLSPlayer.css';
// Import the HLSActions component
import HLSActions from './HLSActions';
import { IssueCastChapter } from '../../../api/hlsApiClient';

// SVG Icons
const PlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3"></polygon>
  </svg>
);

const PauseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="4" width="4" height="16"></rect>
    <rect x="14" y="4" width="4" height="16"></rect>
  </svg>
);

const VolumeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
  </svg>
);

const MuteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
    <line x1="23" y1="9" x2="17" y2="15"></line>
    <line x1="17" y1="9" x2="23" y2="15"></line>
  </svg>
);

const SkipBackIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="19 20 9 12 19 4 19 20"></polygon>
    <line x1="5" y1="19" x2="5" y2="5"></line>
  </svg>
);

const SkipForwardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 4 15 12 5 20 5 4"></polygon>
    <line x1="19" y1="5" x2="19" y2="19"></line>
  </svg>
);

const AlertIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

interface HLSPlayerProps {
  src: string;                      // The URL of the HLS stream
  autoPlay?: boolean;               // Whether to start playing automatically
  muted?: boolean;                  // Whether to start muted
  height?: string | number;         // Player height
  width?: string | number;          // Player width
  className?: string;               // Additional CSS classes
  onError?: (error: Error) => void; // Error callback
  hlsConfig?: Partial<Hls.Config>;  // Additional HLS.js configuration options
  contentId?: string;               // Content ID for feedback and sharing
  showActions?: boolean;            // Whether to show the actions component
  websiteId?: string;               // New: website context for IssueCast publish
  audioOnly?: boolean;              // New: hide video element and treat stream as audio-only
  hideFeedback?: boolean;           // New: hide thumbs up/down buttons
  hideShare?: boolean;              // New: hide share button
  hidePublish?: boolean;            // New: hide publish IssueCast button
  onIssueCastPublished?: () => void; // New: callback after successful IssueCast publish
  initialName?: string;
  initialDescription?: string;
  initialPublishDate?: string;
  chapters?: IssueCastChapter[];    // New: chapters for IssueCast
  articleIds?: string[];            // New: article IDs for AI generation context
  locationName?: string;            // New: location name for IssueCast
  podcastType?: 'multiple_articles' | 'emergency'; // New: podcast type for IssueCast
  classification?: {
    incident_types?: string[];
    locations?: string[];
    severity_tags?: string[];
    timestamp?: string;
  };
}

const HLSPlayer: React.FC<HLSPlayerProps> = ({
  src,
  autoPlay = false,
  muted = false,
  height = 'auto',
  width = '100%',
  className = '',
  onError,
  hlsConfig,
  contentId = '',
  showActions = true,
  websiteId = '',
  audioOnly = false,
  hideFeedback = true,
  hideShare = true,
  hidePublish = false,
  onIssueCastPublished,
  initialName,
  initialDescription,
  initialPublishDate,
  chapters,
  articleIds,
  locationName,
  podcastType = 'multiple_articles',
  classification
}) => {
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  // Keep stable refs for potentially unstable props
  const onErrorRef = useRef<typeof onError>();
  const hlsConfigRef = useRef<Partial<Hls.Config> | undefined>(hlsConfig);
  // Track stream complete state in a ref to avoid effect dependency loops
  const isStreamCompleteRef = useRef(false);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { hlsConfigRef.current = hlsConfig; }, [hlsConfig]);

  // State for player controls
  const [playing, setPlaying] = useState(autoPlay);
  const [volume, setVolume] = useState(muted ? 0 : 1);
  const [isMuted, setIsMuted] = useState(muted);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStreamComplete, setIsStreamComplete] = useState(false); // Track if the stream is VOD (complete)
  // Debug log for prop inputs (once on mount / src change)
  useEffect(() => {
    console.debug('[HLSPlayer] init props', { src, websiteId, contentId });
  }, [src, websiteId, contentId]);

  // Initialize HLS.js
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Reset states
    setLoading(true);
    setError(null);

    // Configure media element for accessibility
    video.setAttribute('aria-label', audioOnly ? 'Audio player' : 'Video player');
    video.setAttribute('tabindex', '0');

    // Check if HLS.js is supported
    if (Hls.isSupported()) {
      // Clean up any existing instance
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }

      // Create a new instance with only the configuration we need,
      // avoiding properties that might cause type conflicts
      const cfg = hlsConfigRef.current || {};
      const hls = new Hls({
        startPosition: cfg.startPosition !== undefined ? cfg.startPosition : -1,
        liveSyncDurationCount: cfg.liveSyncDurationCount !== undefined ? cfg.liveSyncDurationCount : undefined,
        liveMaxLatencyDurationCount: cfg.liveMaxLatencyDurationCount !== undefined ? cfg.liveMaxLatencyDurationCount : undefined,
        initialLiveManifestSize: cfg.initialLiveManifestSize !== undefined ? cfg.initialLiveManifestSize : undefined,
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        maxBufferSize: 60 * 1000 * 1000, // 60MB
        maxBufferHole: 0.5,
      });

      hlsRef.current = hls;

      // Load the source
      hls.loadSource(src);
      hls.attachMedia(video);

      // Handle events
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setLoading(false);
        console.info(`HLS manifest parsed, found ${hls.levels.length} quality level(s)`);

        if (autoPlay) {
          video.play().catch(err => {
            console.warn('Autoplay failed:', err);
            // Many browsers block autoplay - this is expected behavior
            setPlaying(false);
          });
        }
      });

      // Check for stream type changes (EVENT -> VOD)
      hls.on(Hls.Events.LEVEL_UPDATED, (_, data) => {
        const wasComplete = isStreamCompleteRef.current;
        if (data.details?.live === false && !wasComplete) {
          console.info('HLS stream type changed to VOD (was EVENT)');
          isStreamCompleteRef.current = true;
          setIsStreamComplete(true);
        } else if (data.details?.live === true && wasComplete) {
          console.info('HLS stream type changed to EVENT (was VOD)');
          isStreamCompleteRef.current = false;
          setIsStreamComplete(false);
        }
        if (wasComplete !== isStreamCompleteRef.current) {
          console.debug('[HLSPlayer] LEVEL_UPDATED details', { live: data.details?.live, endSN: data.details?.endSN, totalduration: data.details?.totalduration });
        }
      });

      // More detailed event tracking for debugging
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        console.info('HLS.js media attached successfully');
      });

      const logBufferInfo = (data: any) => {
        const videoRanges = data.timeRanges?.video;
        let start = 'N/A';
        let end = 'N/A';

        // Only try to access start/end if videoRanges exists and has entries
        if (videoRanges && videoRanges.length > 0) {
          start = videoRanges.start(0);
          end = videoRanges.end(0);
        }

        console.debug(
          `Buffer appended: type=${data.type}, ` +
          `frag=${data.frag?.sn}, ` +
          `start=${start}, end=${end}`
        );
      };

      hls.on(Hls.Events.BUFFER_APPENDED, (_, data) => {
        logBufferInfo(data);
      });

      // Enhanced error handling
      hls.on(Hls.Events.ERROR, (_, data) => {
        // Log all errors for diagnostic purposes
        console.error('HLS error encountered:', {
          type: data.type,
          details: data.details,
          fatal: data.fatal,
          errorDate: new Date(),
          networkDetails: data.networkDetails,
          response: data.response
        });

        // Handle fatal errors that need recovery
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              // For network errors, always notify the parent component
              const errorMsg = `Network error: ${data.details}`;
              setError(errorMsg);
              setLoading(false);
              
              // Check if this is a 403 or other unrecoverable network error
              const networkDetails = data.networkDetails as any;
              const isUnrecoverable = networkDetails?.status === 403 || networkDetails?.status === 404;
              
              if (isUnrecoverable) {
                // Don't try to recover from 403/404, just notify parent
                console.error('Unrecoverable network error:', networkDetails?.status);
                hls.destroy();
                hlsRef.current = null;
                
                // Try to get response text from the XMLHttpRequest
                let errorDetails = `HTTP ${networkDetails?.status}`;
                let responseText = '';
                
                // The networkDetails is the XMLHttpRequest object
                if (networkDetails?.responseText) {
                  responseText = networkDetails.responseText;
                  errorDetails += `\n\n${responseText}`;
                } else if (networkDetails?.response) {
                  responseText = typeof networkDetails.response === 'string' 
                    ? networkDetails.response 
                    : JSON.stringify(networkDetails.response);
                  errorDetails += `\n\n${responseText}`;
                }
                
                console.log('Response text from network error:', responseText);
                
                const detailedError = new Error(errorMsg);
                (detailedError as any).response = errorDetails;
                (detailedError as any).responseText = responseText;
                onErrorRef.current && onErrorRef.current(detailedError);
              } else {
                // Try to recover network error for other cases
                console.info('Network error, attempting automatic recovery...');
                hls.startLoad();
              }
              break;

            case Hls.ErrorTypes.MEDIA_ERROR:
              // Try to recover media error
              console.info('Media error, attempting automatic recovery...');
              hls.recoverMediaError();
              const mediaErrorMsg = `Media playback error: ${data.details}. Attempting to recover...`;
              setError(mediaErrorMsg);
              break;

            default:
              // Cannot recover
              const fatalErrorMsg = `Playback error: ${data.details}`;
              console.error('Fatal HLS error, cannot recover:', data);
              setError(fatalErrorMsg);
              setLoading(false);
              hls.destroy();
              hlsRef.current = null;
              onErrorRef.current && onErrorRef.current(new Error(fatalErrorMsg));
              break;
          }
        } else {
          // Non-fatal errors can be logged but don't need user notification
          console.warn('Non-fatal HLS error:', data.details);
        }
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // For browsers that support HLS natively (Safari)
      console.info('Using native HLS support (Safari)');
      video.src = src;

      video.addEventListener('loadedmetadata', () => {
        setLoading(false);
        console.info('Native HLS stream loaded successfully');

        if (autoPlay) {
          video.play().catch(err => {
            console.warn('Autoplay failed in native mode:', err);
            setPlaying(false);
          });
        }
      });

      video.addEventListener('error', (e) => {
        const videoElement = e.target as HTMLVideoElement;
        const errorCode = videoElement.error?.code;
        const errorMessage = getMediaErrorMessage(errorCode);

        console.error('Native HLS playback error:', {
          code: errorCode,
          message: errorMessage,
          errorDate: new Date()
        });

        setError(`Unable to play video: ${errorMessage}`);
        setLoading(false);
        onErrorRef.current && onErrorRef.current(new Error(errorMessage));
      });
    } else {
      // HLS not supported at all
      const browserErrorMsg = 'Your browser does not support HLS video playback';
      console.error(browserErrorMsg, {
        browser: navigator.userAgent,
        errorDate: new Date()
      });

      setError(browserErrorMsg);
      setLoading(false);
      onErrorRef.current && onErrorRef.current(new Error(browserErrorMsg));
    }

    // Clean up on unmount
    return () => {
      if (hlsRef.current) {
        console.info('Destroying HLS instance on unmount');
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, autoPlay, audioOnly]);

  // Helper function to get friendly error messages for media errors
  const getMediaErrorMessage = (errorCode?: number): string => {
    switch (errorCode) {
      case 1: // MEDIA_ERR_ABORTED
        return 'Playback was aborted';
      case 2: // MEDIA_ERR_NETWORK
        return 'Network error occurred during download';
      case 3: // MEDIA_ERR_DECODE
        return 'Media playback error. The file may be corrupted';
      case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
        return 'The video format is not supported';
      default:
        return 'An unknown error occurred';
    }
  };

  // Set up video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const onDurationChange = () => {
      setDuration(video.duration);
      console.debug(`Video duration set: ${video.duration}s`);
    };

    const onPlay = () => {
      setPlaying(true);
      console.debug('Video playback started');
    };

    const onPause = () => {
      setPlaying(false);
      console.debug('Video playback paused');
    };

    const onVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
      console.debug(`Volume changed: ${video.volume}, muted: ${video.muted}`);
    };

    const onRateChange = () => {
      setPlaybackRate(video.playbackRate);
      console.debug(`Playback rate changed: ${video.playbackRate}x`);
    };

    const onWaiting = () => {
      console.debug('Video is waiting for more data');
    };

    const onStalled = () => {
      console.debug('Video playback has stalled');
    };

    // Add event listeners
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('durationchange', onDurationChange);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('volumechange', onVolumeChange);
    video.addEventListener('ratechange', onRateChange);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('stalled', onStalled);

    // Clean up
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('durationchange', onDurationChange);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('volumechange', onVolumeChange);
      video.removeEventListener('ratechange', onRateChange);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('stalled', onStalled);
    };
  }, []);

  // Retry playback after error
  const handleRetry = () => {
    if (!videoRef.current) return;

    console.info('User initiated retry after error');

    // Reset error states
    setError(null);
    setLoading(true);

    // Re-initialize the player
    if (Hls.isSupported()) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }

      // Use the same config as initial setup
      const cfg = hlsConfigRef.current || {};
      const hls = new Hls({
        startPosition: cfg.startPosition !== undefined ? cfg.startPosition : -1,
        liveSyncDurationCount: cfg.liveSyncDurationCount !== undefined ? cfg.liveSyncDurationCount : undefined,
        liveMaxLatencyDurationCount: cfg.liveMaxLatencyDurationCount !== undefined ? cfg.liveMaxLatencyDurationCount : undefined,
        initialLiveManifestSize: cfg.initialLiveManifestSize !== undefined ? cfg.initialLiveManifestSize : undefined,
        enableWorker: true,
        lowLatencyMode: true,
        maxBufferLength: 30
      });

      hlsRef.current = hls;

      hls.loadSource(src);
      hls.attachMedia(videoRef.current);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setLoading(false);
        console.info('HLS stream reloaded successfully after retry');
      });
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      videoRef.current.src = src;
      videoRef.current.load();
    }
  };

  // Handle play/pause
  const togglePlay = () => {
    if (!videoRef.current) return;

    if (playing) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(err => {
        console.error('Play failed:', err);
        setError('Could not play video. Check your connection and try again.');
      });
    }
  };

  // Handle mute toggle
  const toggleMute = () => {
    if (!videoRef.current) return;

    const newMutedState = !isMuted;
    videoRef.current.muted = newMutedState;

    // If we're unmuting, restore the previous volume
    if (!newMutedState && volume === 0) {
      const newVolume = 1; // Default to full volume
      videoRef.current.volume = newVolume;
    }
  };

  // Handle volume change
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) return;

    const newVolume = parseFloat(e.target.value);
    videoRef.current.volume = newVolume;

    // If volume is 0, mute; otherwise, unmute
    videoRef.current.muted = newVolume === 0;
  };

  // Handle seeking
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) return;

    const newTime = parseFloat(e.target.value);
    videoRef.current.currentTime = newTime;
  };

  // Handle playback speed
  const handlePlaybackSpeed = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!videoRef.current) return;

    const speed = parseFloat(e.target.value);
    videoRef.current.playbackRate = speed;
  };

  // Handle forward/backward 30s
  const handleSkip = (seconds: number) => {
    if (!videoRef.current) return;

    const newTime = videoRef.current.currentTime + seconds;
    // Ensure we don't go below 0 or above duration
    videoRef.current.currentTime = Math.max(0, Math.min(newTime, videoRef.current.duration));
  };

  // Helper function to format time (e.g., 75 seconds to "1:15")
  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) return '0:00';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`hls-player ${className}`} role="region" aria-label={audioOnly ? 'Audio Player' : 'Video Player'}>
      {/* Media element container (hidden when audioOnly) */}
      {!audioOnly && (
        <div className="hls-player-container">
          <video
            ref={videoRef}
            style={{ width, height }}
            className="hls-player-video"
            playsInline
            onClick={togglePlay}
            tabIndex={0}
          />

          {/* Loading indicator */}
          {loading && !error && (
            <div className="hls-player-loading">
              <div className="hls-player-spinner"></div>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="hls-player-error">
              <div className="hls-player-error-container">
                <div className="hls-player-error-icon">
                  <AlertIcon />
                </div>
                <h3 className="hls-player-error-title">Playback Issue</h3>
                <p className="hls-player-error-message">{error}</p>
                <button
                  onClick={handleRetry}
                  className="hls-player-retry-button"
                  aria-label="Retry playback"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {audioOnly && (
        <>
          <audio
            ref={videoRef as any}
            style={{ display: 'none' }}
          />
          {loading && !error && (
            <div className="hls-player-loading" style={{ marginBottom: 8 }}>
              <div className="hls-player-spinner"></div>
            </div>
          )}
          {error && (
            <div className="hls-player-error" style={{ marginBottom: 8 }}>
              <div className="hls-player-error-container">
                <div className="hls-player-error-icon">
                  <AlertIcon />
                </div>
                <h3 className="hls-player-error-title">Playback Issue</h3>
                <p className="hls-player-error-message">{error}</p>
                <button
                  onClick={handleRetry}
                  className="hls-player-retry-button"
                  aria-label="Retry playback"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Controls */}
      <div className="hls-player-controls" role="toolbar" aria-label={audioOnly ? 'Audio Controls' : 'Video Controls'}>
        {/* Play/Pause button */}
        <button
          onClick={togglePlay}
          className="hls-player-button"
          aria-label={playing ? 'Pause' : 'Play'}
          disabled={loading || !!error}
        >
          {playing ? <PauseIcon /> : <PlayIcon />}
        </button>

        {/* Skip backward 30s */}
        <button
          onClick={() => handleSkip(-30)}
          className="hls-player-button"
          aria-label="Backward 30 seconds"
          disabled={loading || !!error}
        >
          <SkipBackIcon />
          <span className="hls-player-skip-text">30s</span>
        </button>

        {/* Skip forward 30s */}
        <button
          onClick={() => handleSkip(30)}
          className="hls-player-button"
          aria-label="Forward 30 seconds"
          disabled={loading || !!error}
        >
          <span className="hls-player-skip-text">30s</span>
          <SkipForwardIcon />
        </button>

        {/* Time display */}
        <div className="hls-player-time-display" aria-live="polite">
          <span className="sr-only">Current time</span>
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        {/* Scrub bar */}
        <div className="hls-player-scrub-container">
          <input
            type="range"
            min={0}
            max={duration || 100}
            value={currentTime}
            onChange={handleSeek}
            className="hls-player-scrub"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={duration || 100}
            aria-valuenow={currentTime}
            aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
            disabled={loading || !!error || !duration}
          />
        </div>

        {/* Volume/Mute */}
        <button
          onClick={toggleMute}
          className="hls-player-button"
          aria-label={isMuted ? 'Unmute' : 'Mute'}
          disabled={loading || !!error}
        >
          {isMuted ? <MuteIcon /> : <VolumeIcon />}
        </button>

        {/* Volume slider */}
        <div className="hls-player-volume-container">
          <input
            type="range"
            min={0}
            max={1}
            step={0.1}
            value={volume}
            onChange={handleVolumeChange}
            className="hls-player-volume"
            aria-label="Volume"
            disabled={loading || !!error}
          />
        </div>

        {/* Playback speed */}
        <select
          value={playbackRate}
          onChange={handlePlaybackSpeed}
          className="hls-player-speed-select"
          aria-label="Playback Speed"
          disabled={loading || !!error}
        >
          <option value={0.5}>0.5x</option>
          <option value={0.75}>0.75x</option>
          <option value={1}>1x</option>
          <option value={1.25}>1.25x</option>
          <option value={1.5}>1.5x</option>
          <option value={2}>2x</option>
        </select>
        {/* Stream type badge */}
        <div
          className={`hls-player-stream-badge ${isStreamComplete ? 'complete' : 'live'}`}
          aria-label={`Stream type: ${isStreamComplete ? 'VOD complete' : 'Live event (in progress)'}`}
        >
          {isStreamComplete ? 'VOD Complete' : 'Live Event'}
        </div>
      </div>

      {/* Actions component */}
      {showActions && !error && !loading && (
        <HLSActions
          contentId={contentId}
          hlsUrl={src}
          isStreamComplete={isStreamComplete}
          websiteId={websiteId}
          durationSeconds={duration}
          hideFeedback={hideFeedback}
          hideShare={hideShare}
          hidePublish={hidePublish}
          onIssueCastPublished={onIssueCastPublished}
          initialName={initialName}
          initialDescription={initialDescription}
          initialPublishDate={initialPublishDate}
          chapters={chapters}
          articleIds={articleIds}
          locationName={locationName}
          podcastType={podcastType}
          classification={classification}
        />
      )}
    </div>
  );
};

export default HLSPlayer;